import { useContext, useEffect } from 'react';

import { GaugeChart } from '@revelio/replots';

import type { MultiMetricDataMap } from '../DataProvider/MultiMetricProvider';
import { ChartError } from './ChartError';
import { PageExportContext } from './chart-menu/PageExportProvider';
import { useProcessContent } from './TextChart/utils/use-process-content';

type GaugeProps = {
  valueTemplate: string;
  primaryText: string;
  primaryLabel?: string;
  subText: string;
  minLabel?: string;
  maxLabel?: string;
  multiData?: MultiMetricDataMap;
  colorPalette?: string[];
  /** When provided, resolved gauge texts are registered with the PPT export registry. */
  chartId?: string;
};

export const Gauge = ({
  valueTemplate,
  primaryText,
  primaryLabel,
  subText,
  minLabel,
  maxLabel,
  multiData,
  colorPalette,
  chartId,
}: GaugeProps) => {
  // Process the gauge value template if it's not a simple number
  const trimmedValue = valueTemplate.trim();
  const isDirectNumber =
    trimmedValue.length > 0 && !Number.isNaN(Number(trimmedValue));

  const { processedContent: processedValueContent, error: valueError } =
    useProcessContent({
      content: isDirectNumber ? '' : valueTemplate,
      multiData,
    });

  // Process primary and sub text templates
  const { processedContent: processedPrimaryText } = useProcessContent({
    content: primaryText,
    multiData,
  });

  const { processedContent: processedSubText } = useProcessContent({
    content: subText,
    multiData,
  });

  const { processedContent: processedPrimaryLabel } = useProcessContent({
    content: primaryLabel ?? '',
    multiData,
  });

  const primaryTextToDisplay = (() => {
    if (!processedPrimaryText || processedPrimaryText.length < 1) {
      return primaryText;
    }
    return processedPrimaryText;
  })();

  const subTextToDisplay = (() => {
    if (!processedSubText || processedSubText.length < 1) return subText;
    return processedSubText;
  })();

  const primaryLabelToDisplay = (() => {
    if (!primaryLabel) return '';
    if (!processedPrimaryLabel || processedPrimaryLabel.length < 1) {
      return primaryLabel;
    }
    return processedPrimaryLabel;
  })();

  // Strip HTML tags from processed content for gauge text
  // GaugeChart expects plain text, not HTML
  const stripHtml = (html: string | null): string => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').trim();
  };

  // Determine numeric value and display string for the gauge value
  let numericValue: number | null = null;

  if (isDirectNumber) {
    const parsed = Number(trimmedValue);
    if (!Number.isNaN(parsed)) {
      numericValue = parsed;
    }
  } else if (valueTemplate.trim().length > 0 && !valueError) {
    if (processedValueContent) {
      const stripped = stripHtml(processedValueContent);
      const parsed = Number(stripped);
      if (!Number.isNaN(parsed)) {
        numericValue = parsed;
      }
    }
  }

  const isTemplateStillProcessing =
    !isDirectNumber &&
    valueTemplate.trim().length > 0 &&
    processedValueContent === null &&
    !valueError;

  // Clamp numeric value to [0, 1] range
  const clampedValue = Math.max(0, Math.min(1, numericValue ?? 0));

  const mainText = stripHtml(primaryTextToDisplay);
  const subTextDisplay = stripHtml(subTextToDisplay);
  const unitDisplay = stripHtml(primaryLabelToDisplay) || undefined;

  const hasNoData = numericValue === null && !isTemplateStillProcessing;

  const pageExport = useContext(PageExportContext);
  useEffect(() => {
    if (!chartId || !pageExport || hasNoData) return;
    pageExport.updateChartExportInfo(chartId, {
      gaugeMainText: mainText,
      gaugeSubText: subTextDisplay,
      gaugeUnit: unitDisplay,
    });
  }, [chartId, pageExport, mainText, subTextDisplay, unitDisplay, hasNoData]);

  if (hasNoData) {
    return <ChartError message={subTextDisplay || undefined} />;
  }

  return (
    <GaugeChart
      value={clampedValue}
      mainText={mainText}
      subText={subTextDisplay}
      unit={unitDisplay}
      minValue={0}
      maxValue={1}
      minLabel={minLabel}
      maxLabel={maxLabel}
      colorRange={
        colorPalette?.length && colorPalette.length >= 2
          ? colorPalette
          : undefined
      }
    />
  );
};
